export const FACTION_SPECS = {
    WEST: {
        name: 'Frontier',
        title: 'The Frontier',
        creed: 'We built something open, for everyone who wanted in. They would close every door behind them.',
    },
    EAST: {
        name: 'Heritage',
        title: 'The Heritage',
        creed: 'We kept faith with everything that made us. They would tear it down and call it progress.',
    },
};
/** A side's name in a sentence: "Frontier", "Heritage". */
export function factionName(f) {
    return FACTION_SPECS[f].name;
}
/**
 * The cities, sixty-four a side so a full war never repeats one. Coined, not
 * real places: the Frontier's speak of openness, welcome and what is being
 * built; the Heritage's of hearth, faith and what is being kept.
 */
export const CITY_NAMES = {
    WEST: [
        'Brightreach', 'Common Ground', 'Firstlight', 'Horizon Gate', 'Newspan', 'Skyward', 'Openhand', 'Meridian Rise',
        'Openwater', 'Farsight', 'Many Rivers', 'Glasswater', 'Fellowship', 'Waymark', 'Sunward', 'Sparkfield',
        'Accord', 'Outlook', 'Harbourlight', 'Cleargate', 'Crossways', 'Fieldspring', 'Lattice', 'Fairwind',
        'Kindred Point', 'Beaconrise', 'Kiteholm', 'Newcourse', 'Unity Bay', 'Quickwater', 'Longsight', 'Welcomewater',
        'Wideway', 'Freshmeadow', 'Arclight', 'Driftline', 'Highcrossing', 'Seedfield', 'Tallspire', 'Windgate',
        'Mosaic', 'Starling Point', 'Equinox', 'Upward', 'Lumenfield', 'Openstead', 'Clearspring', 'Chorus',
        'Newharbour', 'Daybreak', 'Wayfarer', 'Skylight', 'Openreach', 'Clearview', 'Sunreach', 'Bridgehaven',
        'Lightway', 'Commonfield', 'Brightwater', 'Firstspan', 'Farshore', 'Newfoundry', 'Gladwater', 'Risingtide',
    ],
    EAST: [
        'Oldhallow', 'Hearthstead', 'Kinsward', 'Stonekeep', 'Rootmere', 'Eldermoor', 'Oathbridge', 'Hollowmark',
        'Gravenhall', 'Forebear', 'Keepsake', 'Ashenhold', 'Ironhearth', 'Steadfast', 'Elderwick', 'Oldmere',
        'Bellwarden', 'Candlehold', 'Chapelstone', 'Kinhaven', 'Wardenmoor', 'Threshold', 'Highhallow', 'Barrowdene',
        'Ringwold', 'Mossgate', 'Stillhearth', 'Granitefold', 'Oakenhold', 'Larkhallow', 'Yewmoor', 'Saltkeep',
        'Lanternmere', 'Briarwold', 'Rookstead', 'Tithebarrow', 'Heirloom', 'Crowncairn', 'Elderford', 'Thornhallow',
        'Hearthmoor', 'Vigil', 'Greyoak', 'Fastness', 'Kinmere', 'Lastkeep', 'Homeward', 'Rootwell',
        'Ashkeep', 'Hallowmere', 'Candlewick', 'Oldstone', 'Wardenhall', 'Kinsgate', 'Hearthwold', 'Gravestead',
        'Chantry', 'Mossheart', 'Longbarrow', 'Elderhold', 'Keepwater', 'Oakhearth', 'Stillmoor', 'Bellstead',
    ],
};
/** The opening, as the lobby plays it on a first visit: docs/world/LORE.md, a passage a screen. */
export const LORE_OPENING = [
    [
        'In bleak times, not far from now, humanity’s misadventures finally caught up with us.',
        'We had spent years allowing ourselves to be divided.',
        'Algorithms learned what angered us, what frightened us, and what kept us watching. Those who wanted our attention, our loyalty and our obedience learned how to use them.',
    ],
    [
        'Slowly, disagreement became identity.',
        'Identity became allegiance.',
        'And allegiance became separation.',
        'We became The Frontier and The Heritage.',
    ],
    [
        'Families fractured. Friendships ended. Communities split clean down the middle.',
        'We told ourselves it was safer that way.',
        'A provocation demanded a response. A response demanded retaliation. Every act arrived already wrapped in an explanation for why it had been necessary.',
        'And we believed it.',
    ],
    [
        'There was no moment when everyone stopped and decided that coexistence was over.',
        'That was the problem.',
        'Nobody stopped.',
    ],
    [
        'Our cities are facing one another across a country both sides still call home.',
        'Our families are there. Our history is there.',
        'They tell themselves the same thing.',
        'It no longer matters.',
    ],
];
/** Single lines for the waiting moments: deployment, loading. */
export const LORE_LINES = [
    'Nobody stopped.',
    'Every act arrived already wrapped in an explanation for why it had been necessary.',
    'We told ourselves it was safer that way.',
    'Fear became certainty. Retaliation became defence.',
    'Our families are there. Our history is there.',
    'A provocation demanded a response.',
    'They tell themselves the same thing.',
];
// --- the war's arc ---------------------------------------------------------------
/** The stages of a war, as the lore tells them. A war that goes on past the last is past naming. */
const STAGE_NAMES = ['Provocation', 'Response', 'Retaliation'];
export function stageName(stage) {
    return STAGE_NAMES[stage - 1] ?? 'Nobody stopped';
}
/** "Stage 2 · Response". */
export function stageTitle(stage) {
    return `Stage ${stage} · ${stageName(stage)}`;
}
/**
 * The explanation every act arrives wrapped in (docs/world/LORE.md), as a city's own side
 * tells it. Shown above the numbers it explains. Both sides are told the same
 * lines: they tell themselves the same thing.
 */
export function officialLine(a) {
    const pick = (lines) => lines[Math.abs(Math.floor(a.seed)) % lines.length];
    if (a.device)
        return 'It had to be done, so that it would never have to be done again.';
    if (a.fired > 0 && a.killed > 0) {
        return pick(['A necessary response to their provocation.', 'They left us no choice.', 'Every strike was in defence of our families.']);
    }
    if (a.fired > 0)
        return pick(['Measured. Proportionate. Necessary.', 'We answered, as we said we would.']);
    if (a.struck > 0)
        return pick(['This is what they would do to all of us.', 'They struck first. They always strike first.']);
    return pick(['We are holding the line.', 'Our cities stand. We will not be the ones to stop.']);
}
// --- endings -------------------------------------------------------------------------
/** A city leaving the war, after its name: "Kinsward stopped. …". */
export function defeatPhrase(reason) {
    switch (reason) {
        case 'SURRENDER': return 'stopped. It surrendered and laid down its arms';
        case 'UPRISING': return 'has fallen to its own people. They stopped believing the explanation';
        case 'TAKEOVER': return 'has been taken';
        default: return 'has fallen';
    }
}
/** Where the war's civilian account ends: by how the war ended, and how it ended for you. */
export function closingLine(winner, ownFall) {
    if (ownFall === 'UPRISING')
        return 'In the end, its own people stopped believing.';
    if (!winner)
        return 'Nobody stopped.';
    return 'They tell themselves the same thing.';
}
/** The default closing line. */
export const LORE_CLOSING = closingLine('WEST', null);
/** A lore line for a moment, chosen by any number the moment has (a round, a seat). */
export function loreLine(n) {
    return LORE_LINES[Math.abs(Math.floor(n)) % LORE_LINES.length];
}
//# sourceMappingURL=lore.js.map